/**
 * Flatten sheet (W2-11 batch-tools; DESIGN.md §5.4, §5.8; replaces RPE LAYER-04 "numeric paste + cut"): every
 * additive layer of one property becomes one curve on layer 0, exactly where possible and within a tolerance
 * elsewhere (`Tools.flattenLayers`). Lists the layers that will merge and the sum that replaces them; live ghosts
 * through `useToolGesture`.
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../../app/registry/types';
import { PROP_SPECS } from '../../../core/model/props';
import type { LineProp } from '../../../core/model/types';
import type { TxBuilder } from '../../../core/ops/types';
import { Tools } from '../../../core/tools/index';
import type { ToolReport } from '../../../core/tools/types';
import { EmptyState } from '../../../ui/EmptyState';
import { m } from '../batch.messages';
import s from '../Batch.module.css';
import { EXT, extNumber, ref, remember, toastNotes } from '../batchContext';
import { ExpressionBox, NumberInput, ToolSheet, useText, type SheetStatus } from '../sheetKit';
import { useToolGesture } from '../useToolGesture';

export const PROP_LABEL: Readonly<Record<LineProp, string>> = {
  moveX: m.propMoveX,
  moveY: m.propMoveY,
  rotate: m.propRotate,
  alpha: m.propAlpha,
  speed: m.propSpeed,
  scaleX: m.propScaleX,
  scaleY: m.propScaleY,
  color: m.propColor,
  text: m.propText,
  incline: m.propIncline,
  paint: m.propPaint,
  gif: m.propGif,
};

/** A sensible default tolerance per property (units of the property). */
export function defaultTolerance(prop: LineProp): number {
  switch (prop) {
    case 'alpha':
      return 1;
    case 'rotate':
      return 0.1;
    case 'speed':
      return 0.01;
    default:
      return 0.5;
  }
}

export function FlattenSheet(p: DialogProps<'flatten'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const { line, prop } = props;
  const [layers] = useState(() => {
    if (store === null) return [] as { layer: number; keys: number }[];
    return [...(store.idx.tracksOfLine(line).get(prop) ?? [])].map((t) => {
      const owner = store.doc.tracks.get(t)?.owner;
      return { layer: owner?.kind === 'line' ? owner.layer : 0, keys: store.idx.keysByTrack(t).length };
    });
  });
  const [tolerance, setTolerance] = useState(() =>
    extNumber(services, `${EXT.flattenTolerance}.${prop}`, defaultTolerance(prop), 0.0001, 10000),
  );
  const layered = PROP_SPECS[prop].layered;
  const build = useMemo(
    () =>
      layered && layers.length >= 2
        ? (tx: TxBuilder): ToolReport => Tools.flattenLayers(tx, { line, prop, tolerance })
        : null,
    [layered, layers, line, prop, tolerance],
  );
  const gesture = useToolGesture<ToolReport>({ services, label: ref(m.txnFlatten), command: 'tools.flatten', build });

  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  const lineName = store?.doc.lines.get(line)?.name ?? '';
  if (build === null) {
    return (
      <ToolSheet
        title={text.t(m.flattenTitle)}
        status={{ kind: 'none' }}
        applyLabel={text.t(m.apply)}
        applyDisabledReason={ref(layered ? m.flattenOneLayer : m.flattenNotLayered)}
        onApply={() => undefined}
        onCancel={cancel}
      >
        <EmptyState icon="Layers" title={ref(layered ? m.flattenOneLayer : m.flattenNotLayered)} />
      </ToolSheet>
    );
  }

  const outcome = gesture.outcome;
  let status: SheetStatus = { kind: 'none' };
  if (gesture.status === 'paused') status = { kind: 'warn', text: text.t(m.previewPaused) };
  else if (outcome !== null && !outcome.ok) status = { kind: 'error', text: text.tr(outcome.reason) };
  else if (outcome?.ok === true) {
    const err = outcome.value.maxError ?? 0;
    status =
      err === 0
        ? { kind: 'ok', text: text.t(m.flattenExactStatus, { keys: outcome.value.changed }) }
        : {
            kind: 'warn',
            text: text.t(m.flattenBakedStatus, { keys: outcome.value.changed, error: Number(err.toPrecision(3)) }),
          };
  }

  const apply = (): void => {
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    remember(services, `${EXT.flattenTolerance}.${prop}`, tolerance);
    services.ui.toast(ref(m.flattenDone, { count: layers.length }), { kind: 'success', undo: out.txn !== null });
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };

  return (
    <ToolSheet
      title={text.t(m.flattenTitle)}
      subtitle={text.t(m.flattenSubtitle, { prop: text.t(PROP_LABEL[prop]), line: lineName })}
      status={status}
      applyLabel={text.t(m.apply)}
      onApply={apply}
      onCancel={cancel}
    >
      <ul className={s.preview} aria-label={text.t(m.flattenLayers)}>
        {layers.map((l) => (
          <li key={l.layer}>{text.t(m.flattenLayerItem, { layer: l.layer, keys: l.keys })}</li>
        ))}
      </ul>
      <ExpressionBox
        services={services}
        label={text.t(m.equivalentExpression)}
        code={`${text.t(PROP_LABEL[prop])} = ${layers.map((l) => text.t(m.flattenFormulaTerm, { layer: l.layer })).join(' + ')}`}
        note={text.t(m.flattenFormulaNote)}
      />
      <NumberInput
        label={text.t(m.tolerance)}
        value={tolerance}
        min={0.0001}
        max={10000}
        step={0.1}
        onChange={setTolerance}
      />
      <p className={s.hint}>{text.t(m.flattenHint)}</p>
    </ToolSheet>
  );
}
